import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Container, SectionTitle, Button } from '../../styles/StyledComponents';

const OfferSection = styled.section`
  padding: 6rem 0;
  background-color: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.white};
`;

const OfferContent = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
  }
`;

const OfferTextContent = styled(motion.div)`
  flex: 1;
`;

const OfferDescription = styled.p`
  font-size: 1.1rem;
  margin: 1.5rem 0;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.white};
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0;
`;

const BenefitItem = styled(motion.li)`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const BenefitIcon = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  flex-shrink: 0;
  color: white;
  font-size: 16px;
`;

const BenefitText = styled.p`
  margin: 0;
  font-size: 1.05rem;
  color: ${({ theme }) => theme.colors.white};
`;

const OfferCTA = styled.p`
  font-size: 1.2rem;
  margin: 2rem 0;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.white};
`;

const OfferImageContainer = styled(motion.div)`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const OfferIllustration = styled.div`
  max-width: 500px;
  height: auto;
  position: relative;
  
  img {
    border-radius: 12px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const SectionTitleWithIcon = styled(SectionTitle)`
  display: flex;
  align-items: center;
  
  .material-icons {
    margin-right: 0.5rem;
    font-size: 2rem;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const EthicalOffer = () => {
  return (
    <OfferSection id="ethical-offer">
      <Container>
        <OfferContent>
          <OfferTextContent
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <SectionTitleWithIcon light>
              <span className="material-icons">rocket_launch</span>
              Let AI Handle the Grind So You Can Grow
            </SectionTitleWithIcon>
            <OfferDescription>
              We help you automate the tedious stuff so you can close more deals, deliver faster, and make smarter decisions without drowning in dashboards.
            </OfferDescription>
            <BenefitsList>
              <BenefitItem>
                <BenefitIcon>✓</BenefitIcon>
                <BenefitText>More qualified leads, less manual outreach</BenefitText>
              </BenefitItem>
              <BenefitItem>
                <BenefitIcon>✓</BenefitIcon>
                <BenefitText>Faster project delivery, fewer dropped balls</BenefitText>
              </BenefitItem>
              <BenefitItem>
                <BenefitIcon>✓</BenefitIcon>
                <BenefitText>No more spreadsheet gymnastics—just clean insights</BenefitText>
              </BenefitItem>
              <BenefitItem>
                <BenefitIcon>✓</BenefitIcon>
                <BenefitText>Systems that scale without adding headcount</BenefitText>
              </BenefitItem>
            </BenefitsList>
            <OfferCTA>
              Let's build something that pays off in the first 90 days.
            </OfferCTA>
            <Button 
              as={motion.a}
              href="https://cal.com/scott-laughlin-i6yml3/30min"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.7 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              light
            >
              Schedule Your Free Consultation
            </Button>
          </OfferTextContent>
          <OfferImageContainer
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <OfferIllustration>
              <img 
                src="/images/before-and-after.png" 
                alt="Before and after AI automation: Left side shows a stressed worker with cluttered workspace, right side shows a calm worker with organized workspace" 
                style={{ width: '100%', height: 'auto', borderRadius: '20px' }}
              />
            </OfferIllustration>
          </OfferImageContainer>
        </OfferContent>
      </Container>
    </OfferSection>
  );
};

export default EthicalOffer;
